// All 14 built-in A2UI functions

export type CustomFunctions = Record<string, (args: Record<string, unknown>) => unknown>;

type BuiltinFn = (args: Record<string, unknown>) => unknown;

// -- Validation functions --

function required(args: Record<string, unknown>): boolean {
	const { value } = args;
	if (value === null || value === undefined) {
		return false;
	}
	if (typeof value === 'string') {
		return value.trim().length > 0;
	}
	if (Array.isArray(value)) {
		return value.length > 0;
	}
	return true;
}

function regex(args: Record<string, unknown>): boolean {
	const { value, pattern } = args;
	if (typeof value !== 'string' || typeof pattern !== 'string') {
		return false;
	}
	return new RegExp(pattern).test(value);
}

function length(args: Record<string, unknown>): boolean {
	const { value, min, max } = args;
	const str = typeof value === 'string' ? value : String(value ?? '');
	const len = str.length;
	if (typeof min === 'number' && len < min) {
		return false;
	}
	if (typeof max === 'number' && len > max) {
		return false;
	}
	return true;
}

function numeric(args: Record<string, unknown>): boolean {
	const { value, min, max } = args;
	const num = typeof value === 'number' ? value : Number(value);
	if (isNaN(num)) {
		return false;
	}
	if (typeof min === 'number' && num < min) {
		return false;
	}
	if (typeof max === 'number' && num > max) {
		return false;
	}
	return true;
}

function email(args: Record<string, unknown>): boolean {
	const { value } = args;
	if (typeof value !== 'string') {
		return false;
	}
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

// -- Formatting functions --

function formatString(args: Record<string, unknown>): string {
	const { value } = args;
	if (typeof value !== 'string') {
		return String(value ?? '');
	}
	// Simple ${key} interpolation from remaining args
	return value.replace(/\$\{(\w+)\}/g, (_, key) => {
		const replacement = args[key];
		return replacement != null ? String(replacement) : '';
	});
}

function formatNumber(args: Record<string, unknown>): string {
	const { value, decimals, grouping } = args;
	const num = typeof value === 'number' ? value : Number(value);
	if (isNaN(num)) {
		return String(value ?? '');
	}
	const opts: Intl.NumberFormatOptions = {};
	if (typeof decimals === 'number') {
		opts.minimumFractionDigits = decimals;
		opts.maximumFractionDigits = decimals;
	}
	if (grouping === false) {
		opts.useGrouping = false;
	}
	return new Intl.NumberFormat(undefined, opts).format(num);
}

function formatCurrency(args: Record<string, unknown>): string {
	const { value, currency, decimals, grouping } = args;
	const num = typeof value === 'number' ? value : Number(value);
	if (isNaN(num) || typeof currency !== 'string') {
		return String(value ?? '');
	}
	const opts: Intl.NumberFormatOptions = {
		style: 'currency',
		currency,
	};
	if (typeof decimals === 'number') {
		opts.minimumFractionDigits = decimals;
		opts.maximumFractionDigits = decimals;
	}
	if (grouping === false) {
		opts.useGrouping = false;
	}
	return new Intl.NumberFormat(undefined, opts).format(num);
}

function formatDate(args: Record<string, unknown>): string {
	const { value, format } = args;
	if (typeof value !== 'string') {
		return String(value ?? '');
	}
	const date = new Date(value);
	if (isNaN(date.getTime())) {
		return String(value);
	}
	// Map common Unicode TR35 patterns to Intl.DateTimeFormat options
	if (typeof format === 'string') {
		const opts: Intl.DateTimeFormatOptions = {};
		if (format.includes('yyyy') || format.includes('YYYY')) {
			opts.year = 'numeric';
		}
		if (format.includes('MM')) {
			opts.month = '2-digit';
		} else if (format.includes('MMM')) {
			opts.month = 'short';
		} else if (format.includes('MMMM')) {
			opts.month = 'long';
		}
		if (format.includes('dd') || format.includes('DD')) {
			opts.day = '2-digit';
		}
		if (format.includes('HH') || format.includes('hh')) {
			opts.hour = '2-digit';
		}
		if (format.includes('mm')) {
			opts.minute = '2-digit';
		}
		if (format.includes('ss')) {
			opts.second = '2-digit';
		}
		if (Object.keys(opts).length > 0) {
			return new Intl.DateTimeFormat(undefined, opts).format(date);
		}
	}
	return date.toLocaleDateString();
}

function pluralize(args: Record<string, unknown>): string {
	const { value } = args;
	const num = typeof value === 'number' ? value : Number(value);
	const rules = new Intl.PluralRules();
	const category = rules.select(num);

	const result = args[category] ?? args['other'];
	return typeof result === 'string'
		? result.replace('{value}', String(num))
		: String(result ?? num);
}

// -- Logic functions --

function and(args: Record<string, unknown>): boolean {
	const { values } = args;
	if (!Array.isArray(values)) {
		return Boolean(values);
	}
	return values.every(Boolean);
}

function or(args: Record<string, unknown>): boolean {
	const { values } = args;
	if (!Array.isArray(values)) {
		return Boolean(values);
	}
	return values.some(Boolean);
}

function not(args: Record<string, unknown>): boolean {
	return !args.value;
}

// -- Action functions --

function openUrl(args: Record<string, unknown>): void {
	const { url } = args;
	if (typeof url === 'string') {
		window.open(url, '_blank', 'noopener,noreferrer');
	}
}

// -- Registry --

const builtinFunctions: Record<string, BuiltinFn> = {
	required,
	regex,
	length,
	numeric,
	email,
	formatString,
	formatNumber,
	formatCurrency,
	formatDate,
	pluralize,
	and,
	or,
	not,
	openUrl,
};

export function executeFunctionCall(
	name: string,
	args: Record<string, unknown>,
	customFunctions?: CustomFunctions,
): unknown {
	// Check custom functions first (allows overriding builtins)
	if (customFunctions?.[name]) {
		return customFunctions[name](args);
	}
	const fn = builtinFunctions[name];
	if (!fn) {
		return undefined;
	}
	return fn(args);
}
